import { c as _c } from "react/compiler-runtime";
import Translate from "@mui/icons-material/Translate";
import { alpha, Box, Divider, FormControl, InputLabel, List, ListItem, ListItemButton, ListItemIcon, ListItemText, ListSubheader, MenuItem, Select, SwipeableDrawer, Typography, useTheme } from "@mui/material";
import { signOut, useSession } from "next-auth/react";
import Link from "next/link";
import { useRouter as useAppRouter, usePathname, useSearchParams } from "next/navigation";
import React from "react";
import { useOptionalCookieConsentContext } from "#/features/cookieConsent/context/CookieConsentContext";
import { sidePanelProfileHref } from "#/features/menuSidePanel/lib/sidePanelProfileHref";
import type { Locales } from "#/i18n/i18n-types";
import { loadLocaleAsync } from "#/i18n/i18n-util.async";
import { localeLabels, localesForLanguagePicker } from "#/i18n/labels";
import { localeSwitchHref } from "#/i18n/localeSwitchHref";
import { parseSelectLocaleValue } from "#/i18n/parseSelectLocaleValue";
import { setStoredLocale } from "#/shared/browser-storage/localeStorage";
import { useI18nContext } from "#/shared/hooks";
import { GITHUB_URL } from "#/constants";
type NavItemProps = {
  title: string;
  onClick?: () => void;
  href?: string;
};
const NavItem: React.FC<NavItemProps> = t0 => {
  const $ = _c(7);
  const {
    title,
    onClick,
    href
  } = t0;
  const component = href ? "a" : "button";
  let t1;
  if ($[0] !== title) {
    t1 = <ListItemText primary={title} />;
    $[0] = title;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== component || $[3] !== href || $[4] !== onClick || $[5] !== t1) {
    t2 = <ListItem disablePadding={true}><ListItemButton component={component} href={href} onClick={onClick}>{t1}</ListItemButton></ListItem>;
    $[2] = component;
    $[3] = href;
    $[4] = onClick;
    $[5] = t1;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  return t2;
};
type SidePanelProps = {
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
};
export const SidePanel: React.FC<SidePanelProps> = t0 => {
  const $ = _c(88);
  const {
    isOpen,
    setIsOpen
  } = t0;
  const appRouter = useAppRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const {
    LL,
    locale: activeLocale
  } = useI18nContext();
  const theme = useTheme();
  const session = useSession();
  const cookieConsent = useOptionalCookieConsentContext();
  let t1;
  if ($[0] !== session.data || $[1] !== session.status) {
    t1 = session.status === "authenticated" ? sidePanelProfileHref(session.data) : null;
    $[0] = session.data;
    $[1] = session.status;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const profileHref = t1;
  let t2;
  if ($[3] !== cookieConsent || $[4] !== setIsOpen) {
    t2 = () => {
      cookieConsent?.openCookieSettings();
      setIsOpen(false);
    };
    $[3] = cookieConsent;
    $[4] = setIsOpen;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  const handleOpenCookieSettings = t2;
  let t3;
  if ($[6] !== activeLocale || $[7] !== appRouter || $[8] !== pathname || $[9] !== searchParams || $[10] !== setIsOpen) {
    t3 = async newLocale => {
      if (newLocale === activeLocale) {
        return;
      }
      setStoredLocale(newLocale);
      await loadLocaleAsync(newLocale);
      const href = localeSwitchHref(pathname ?? "/", searchParams.toString(), newLocale);
      appRouter.push(href);
      setIsOpen(false);
    };
    $[6] = activeLocale;
    $[7] = appRouter;
    $[8] = pathname;
    $[9] = searchParams;
    $[10] = setIsOpen;
    $[11] = t3;
  } else {
    t3 = $[11];
  }
  const handleChangeLocale = t3;
  let t4;
  let t5;
  if ($[12] !== setIsOpen) {
    t4 = () => setIsOpen(true);
    t5 = () => setIsOpen(false);
    $[12] = setIsOpen;
    $[13] = t4;
    $[14] = t5;
  } else {
    t4 = $[13];
    t5 = $[14];
  }
  let t6;
  if ($[15] !== theme.appDesign.surfaceHigh) {
    t6 = alpha(theme.appDesign.surfaceHigh, 0.88);
    $[15] = theme.appDesign.surfaceHigh;
    $[16] = t6;
  } else {
    t6 = $[16];
  }
  let t7;
  if ($[17] !== theme.appDesign.outline) {
    t7 = alpha(theme.appDesign.outline, 0.16);
    $[17] = theme.appDesign.outline;
    $[18] = t7;
  } else {
    t7 = $[18];
  }
  const t8 = `1px solid ${t7}`;
  let t9;
  if ($[19] !== t6 || $[20] !== t8) {
    t9 = {
      paper: {
        sx: {
          background: t6,
          backdropFilter: "blur(20px)",
          borderLeft: t8
        }
      }
    };
    $[19] = t6;
    $[20] = t8;
    $[21] = t9;
  } else {
    t9 = $[21];
  }
  let t10;
  if ($[22] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = {
      minWidth: "22vw"
    };
    $[22] = t10;
  } else {
    t10 = $[22];
  }
  let t11;
  if ($[23] !== LL) {
    t11 = LL.MAIN_MENU();
    $[23] = LL;
    $[24] = t11;
  } else {
    t11 = $[24];
  }
  let t12;
  if ($[25] !== t11) {
    t12 = <ListSubheader disableSticky={true}>{t11}</ListSubheader>;
    $[25] = t11;
    $[26] = t12;
  } else {
    t12 = $[26];
  }
  let t13;
  if ($[27] !== LL || $[28] !== profileHref) {
    t13 = profileHref ? <Link href={profileHref}><NavItem title={LL.PROFILE()} /></Link> : null;
    $[27] = LL;
    $[28] = profileHref;
    $[29] = t13;
  } else {
    t13 = $[29];
  }
  let t14;
  if ($[30] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = <NavItem title="GitHub" href={GITHUB_URL} />;
    $[30] = t14;
  } else {
    t14 = $[30];
  }
  let t15;
  if ($[31] !== LL) {
    t15 = LL.FEEDBACK();
    $[31] = LL;
    $[32] = t15;
  } else {
    t15 = $[32];
  }
  let t16;
  if ($[33] !== t15) {
    t16 = <NavItem title={t15} href={`${GITHUB_URL}/issues`} />;
    $[33] = t15;
    $[34] = t16;
  } else {
    t16 = $[34];
  }
  let t17;
  if ($[35] !== LL) {
    t17 = LL.LOGOUT();
    $[35] = LL;
    $[36] = t17;
  } else {
    t17 = $[36];
  }
  let t18;
  if ($[37] !== t17) {
    t18 = <NavItem title={t17} onClick={_temp} />;
    $[37] = t17;
    $[38] = t18;
  } else {
    t18 = $[38];
  }
  let t19;
  if ($[39] !== t12 || $[40] !== t13 || $[41] !== t16 || $[42] !== t18) {
    t19 = <List subheader={t12}>{t13}{t14}{t16}{t18}</List>;
    $[39] = t12;
    $[40] = t13;
    $[41] = t16;
    $[42] = t18;
    $[43] = t19;
  } else {
    t19 = $[43];
  }
  let t20;
  if ($[44] === Symbol.for("react.memo_cache_sentinel")) {
    t20 = <Divider />;
    $[44] = t20;
  } else {
    t20 = $[44];
  }
  let t21;
  if ($[45] !== LL) {
    t21 = LL.SETTINGS();
    $[45] = LL;
    $[46] = t21;
  } else {
    t21 = $[46];
  }
  let t22;
  if ($[47] !== t21) {
    t22 = <ListSubheader disableSticky={true}>{t21}</ListSubheader>;
    $[47] = t21;
    $[48] = t22;
  } else {
    t22 = $[48];
  }
  let t23;
  if ($[49] === Symbol.for("react.memo_cache_sentinel")) {
    t23 = <ListItemIcon><Translate /></ListItemIcon>;
    $[49] = t23;
  } else {
    t23 = $[49];
  }
  let t24;
  if ($[50] !== LL) {
    t24 = LL.LANGUAGE();
    $[50] = LL;
    $[51] = t24;
  } else {
    t24 = $[51];
  }
  let t25;
  if ($[52] !== t24) {
    t25 = <InputLabel id="side-panel-language-label">{t24}</InputLabel>;
    $[52] = t24;
    $[53] = t25;
  } else {
    t25 = $[53];
  }
  let t26;
  if ($[54] !== LL) {
    t26 = LL.LANGUAGE();
    $[54] = LL;
    $[55] = t26;
  } else {
    t26 = $[55];
  }
  const t27 = activeLocale ?? "en";
  let t28;
  if ($[56] !== handleChangeLocale) {
    t28 = event => {
      const newLocale_0 = parseSelectLocaleValue(String(event.target.value));
      if (newLocale_0) {
        handleChangeLocale(newLocale_0);
      }
    };
    $[56] = handleChangeLocale;
    $[57] = t28;
  } else {
    t28 = $[57];
  }
  let t29;
  if ($[58] === Symbol.for("react.memo_cache_sentinel")) {
    t29 = localesForLanguagePicker.map(_temp2);
    $[58] = t29;
  } else {
    t29 = $[58];
  }
  let t30;
  if ($[59] !== t26 || $[60] !== t27 || $[61] !== t28) {
    t30 = <Select labelId="side-panel-language-label" label={t26} value={t27} onChange={t28}>{t29}</Select>;
    $[59] = t26;
    $[60] = t27;
    $[61] = t28;
    $[62] = t30;
  } else {
    t30 = $[62];
  }
  let t31;
  if ($[63] !== t25 || $[64] !== t30) {
    t31 = <ListItem>{t23}<FormControl fullWidth={true}>{t25}{t30}</FormControl></ListItem>;
    $[63] = t25;
    $[64] = t30;
    $[65] = t31;
  } else {
    t31 = $[65];
  }
  let t32;
  if ($[66] !== LL) {
    t32 = LL.PRIVACY_POLICY();
    $[66] = LL;
    $[67] = t32;
  } else {
    t32 = $[67];
  }
  let t33;
  if ($[68] !== t32) {
    t33 = <NavItem title={t32} href="/privacy" />;
    $[68] = t32;
    $[69] = t33;
  } else {
    t33 = $[69];
  }
  let t34;
  if ($[70] !== LL || $[71] !== cookieConsent || $[72] !== handleOpenCookieSettings) {
    t34 = cookieConsent ? <NavItem title={LL.COOKIE_SETTINGS_LINK()} onClick={handleOpenCookieSettings} /> : null;
    $[70] = LL;
    $[71] = cookieConsent;
    $[72] = handleOpenCookieSettings;
    $[73] = t34;
  } else {
    t34 = $[73];
  }
  let t35;
  if ($[74] !== t22 || $[75] !== t31 || $[76] !== t33 || $[77] !== t34) {
    t35 = <List subheader={t22}>{t31}{t33}{t34}</List>;
    $[74] = t22;
    $[75] = t31;
    $[76] = t33;
    $[77] = t34;
    $[78] = t35;
  } else {
    t35 = $[78];
  }
  let t36;
  if ($[79] !== t19 || $[80] !== t35) {
    t36 = <Box role="presentation" sx={t10}>{t19}{t20}{t35}</Box>;
    $[79] = t19;
    $[80] = t35;
    $[81] = t36;
  } else {
    t36 = $[81];
  }
  let t37;
  if ($[82] !== isOpen || $[83] !== t36 || $[84] !== t4 || $[85] !== t5 || $[86] !== t9) {
    t37 = <><SwipeableDrawer anchor="right" open={isOpen} onOpen={t4} onClose={t5} slotProps={t9}>{t36}</SwipeableDrawer></>;
    $[82] = isOpen;
    $[83] = t36;
    $[84] = t4;
    $[85] = t5;
    $[86] = t9;
    $[87] = t37;
  } else {
    t37 = $[87];
  }
  return t37;
};
function _temp() {
  return signOut();
}
function _temp2(localeOption) {
  return <MenuItem key={localeOption} value={localeOption}><Typography>{localeLabels[localeOption]}</Typography></MenuItem>;
}